import * as React from "react";
import { FileUploader } from "baseui/file-uploader";
import {
  BulkUploadOrdersResult,
  useBulkUploadOrdersMutation,
} from "@api/graphql/generated/generated-types";
import { AuthContext } from "@src/auth/components/AuthProvider";
import CourieButton from "@src/common/components/Button/Button";
import { ArrowUpTrayIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import {
  BaseModal,
  BaseModalBody,
  BaseModalHeader,
  Button,
  Tooltip,
} from "@src/common/components";

type Props = {
  onUploadComplete: () => void;
};

const FileUploaderOrderCreate: React.FC<Props> = ({ onUploadComplete }) => {
  const { courierId } = React.useContext(AuthContext);
  const [bulkUploadOrdersMutation, { loading, error }] =
    useBulkUploadOrdersMutation();

  // states
  const [isUploadModalOpen, setIsUploadModalOpen] = React.useState(false);
  const [bulkUploadResult, setBulkUploadResult] =
    React.useState<BulkUploadOrdersResult | null>();

  const handleFileUpload = (acceptedFiles: File[]) => {
    if (!courierId) return;
    if (acceptedFiles.length > 0) {
      setBulkUploadResult(null);
      const file = acceptedFiles[0];
      bulkUploadOrdersMutation({
        variables: {
          courierId,
          file,
        },
      }).then((result) => {
        if (result.data?.bulkUploadOrders) {
          setBulkUploadResult(result.data.bulkUploadOrders);
          // Async path imports out-of-band and emails on completion, so nothing is
          // created yet — don't refresh the orders view (it would race the cron).
          if (
            !result.data.bulkUploadOrders.error &&
            !result.data.bulkUploadOrders.isAsynchronous
          ) {
            onUploadComplete();
          }
        }
      });
    }
  };

  React.useEffect(() => {
    if (isUploadModalOpen) {
      setBulkUploadResult(null);
    }
  }, [isUploadModalOpen]);

  const renderErrors = () => {
    if (!bulkUploadResult?.error) return null;

    return (
      <div className="mt-4 p-4 bg-red-100 border border-red-400 rounded-md">
        {bulkUploadResult.error.errorMessages.length > 0 && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-red-600">File Errors:</h3>
            <ul className="list-disc list-inside text-red-600">
              {bulkUploadResult.error.errorMessages.map((msg, index) => (
                <li key={index}>{msg}</li>
              ))}
            </ul>
          </div>
        )}
        {bulkUploadResult.error.lineErrors.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-red-600">
              Line By Line:
            </h3>
            {bulkUploadResult.error.lineErrors.map((lineError, index) => (
              <div key={index} className="mb-2">
                <p className="font-semibold text-red-600">
                  Line {lineError.lineNumber}:
                </p>
                <ul className="list-disc list-inside text-red-600">
                  {lineError.cellErrors.map((cellError, cellIndex) => (
                    <li key={cellIndex}>
                      {cellError.columnName
                        ? `${cellError.columnName}: ${cellError.errorMessage}`
                        : cellError.errorMessage}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Tooltip content="Upload New Orders" placement="bottom">
        <CourieButton
          size={"xs"}
          color={"secondary"}
          outline
          onClick={() => setIsUploadModalOpen(true)}
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
        </CourieButton>
      </Tooltip>
      <BaseModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        overrides={{
          Root: {
            style: {
              zIndex: 20,
            },
          },
        }}
      >
        <BaseModalHeader>Upload Orders CSV</BaseModalHeader>
        <BaseModalBody>
          <p className="mb-4">
            Please refer to
            <a
              href="/orders-example.csv"
              className="font-mono bg-gray-200 px-2 py-1 rounded mx-1 hover:bg-gray-300"
              download
            >
              example.csv
            </a>
            for the required column names and their expected formats.
          </p>
          <FileUploader
            onDrop={(acceptedFiles) => {
              handleFileUpload(acceptedFiles);
            }}
            onCancel={() => {
              // do nothing
            }}
          />
          {loading && <p>Uploading...</p>}
          {error && <p className="text-red-600">Error: {error.message}</p>}
          {bulkUploadResult?.isAsynchronous && (
            <p className="text-green-600 flex mt-2">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              We have started uploading your CSV and will send you an email once
              we are done.
            </p>
          )}
          {bulkUploadResult &&
            !bulkUploadResult.isAsynchronous &&
            bulkUploadResult.numSuccess > 0 && (
              <p className="text-green-600 flex mt-2">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                Upload successful: {bulkUploadResult.numSuccess} orders uploaded.
              </p>
            )}
          {renderErrors()}
        </BaseModalBody>
      </BaseModal>
    </>
  );
};

export default FileUploaderOrderCreate;
